import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faPlus,
  faFilter,
  faPen,
  faTrash,
  faSearch,
  faXmark,
  faLayerGroup,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import TemplateService, {
  type TemplateListFilters,
} from '../../../../AppSettings/services/TemplateService';
import ClientService from '../../../../AppSettings/services/ClientService';
import EmployeeService from '../../../../AppSettings/services/EmployeeService';
import ToastService from '../../../../AppSettings/services/ToastService';
import './TemplatesPage.css';

/**
 * Workflow Template Builder - Template List Page
 * 
 * PRD3: Workflow Template Builder
 * This page provides the user interface for browsing and managing workflow templates.
 * 
 * Features:
 * - View templates in a table with key metadata (name, client, owner, last updated)
 * - Filter templates by client, owner, and search keywords
 * - Pagination for large template lists
 * - Click row to open template editor
 * - Create new template button
 */

interface FilterState {
  clientId: string;
  ownerId: string;
  search: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    clientId: '',
    ownerId: '',
    search: '',
  });
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteTemplateName, setDeleteTemplateName] = useState<string>('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  
  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  
  // Update filters when debounced search changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
    setPage(1); // Reset to first page on search
  }, [debouncedSearch]);
  
  // Build query filters
  const queryFilters: TemplateListFilters = useMemo(() => ({
    page,
    limit,
    clientId: filters.clientId || undefined,
    ownerId: filters.ownerId || undefined,
    search: filters.search || undefined,
  }), [filters, page, limit]);
  
  // Fetch templates
  const {
    data: templatesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['templates', queryFilters],
    queryFn: () => TemplateService.listTemplates(queryFilters),
    staleTime: 30000, // 30 seconds
  });
  
  // Fetch clients for filter dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: ClientService.getClients,
    staleTime: 300000, // 5 minutes
  });
  
  // Fetch employees for filter dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: EmployeeService.getEmployees,
    staleTime: 300000, // 5 minutes
  });
  
  // Prepare client options for dropdown
  const clientOptions: SelectOption[] = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.map((client) => {
      const clientId = client.ClientID || client.clientId || '';
      const firstName = client.FirstName || client.first_name || '';
      const lastName = client.LastName || client.last_name ? ` ${client.LastName || client.last_name}` : '';
      const company = client.Company || client.company ? ` (${client.Company || client.company})` : '';
      return {
        value: clientId,
        label: `${clientId} - ${firstName}${lastName}${company}`.trim() || clientId,
      };
    });
  }, [clientsData]);
  
  // Prepare employee options for dropdown
  const employeeOptions: SelectOption[] = useMemo(() => {
    if (!employeesData) return [];
    return employeesData.map((employee) => ({
      value: employee.EmployeeID || '',
      label: `${employee.FirstName || ''} ${employee.LastName || ''}`.trim() || employee.EmployeeID || '',
    }));
  }, [employeesData]);
  
  // Filter handlers
  const handleClientFilter = useCallback((clientId: string) => {
    setFilters(prev => ({ ...prev, clientId }));
    setPage(1);
  }, []);
  
  const handleOwnerFilter = useCallback((ownerId: string) => {
    setFilters(prev => ({ ...prev, ownerId }));
    setPage(1);
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters({
      clientId: '',
      ownerId: '',
      search: '',
    });
    setSearchInput('');
    setPage(1);
  }, []);
  
  // Row click handler - navigate to editor
  const handleRowClick = useCallback((templateId: string) => {
    navigate(`/templates/${templateId}/edit`);
  }, [navigate]);
  
  // Create template handler
  const handleCreateTemplate = useCallback(() => {
    navigate('/templates/create');
  }, [navigate]);

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) => TemplateService.deleteTemplate(templateId),
    onSuccess: () => {
      ToastService.success('Template deleted successfully');
      setDeleteConfirmId(null);
      setDeleteTemplateName('');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to delete template');
    },
  });

  const handleDeleteClick = useCallback((templateId: string, templateName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmId(templateId);
    setDeleteTemplateName(templateName);
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirmId) {
      deleteTemplateMutation.mutate(deleteConfirmId);
    }
  }, [deleteConfirmId, deleteTemplateMutation]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
    setDeleteTemplateName('');
  }, []);

  // Get templates and pagination from response
  const templates = templatesData?.templates || [];
  const pagination = templatesData?.pagination;

  // Number of active filters (for the Filters-button badge and panel-header "Clear all")
  const activeFilterCount = [filters.clientId, filters.ownerId, filters.search].filter(Boolean).length;

  // Format date for display
  const formatDateForDisplay = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };
  
  // Get client name from ID
  const getClientName = (clientId: string | null | undefined): string => {
    if (!clientId) return 'All Clients';
    const client = clientOptions.find(opt => opt.value === clientId);
    return client ? client.label : clientId;
  };
  
  // Get owner name from ID
  const getOwnerName = (ownerId: string | null | undefined): string => {
    if (!ownerId) return '—';
    const employee = employeeOptions.find(opt => opt.value === ownerId);
    return employee ? employee.label : ownerId;
  };
  
  return (
    <div className="templates-page">
      <div className="templates-header">
        <div className="header-content">
          <div className="header-title-row">
            <button
              className="templates-back-button"
              onClick={() => navigate('/createProjects')}
              aria-label="Back to create projects"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="header-text">
              <h1>Workflow Templates</h1>
              <p className="page-description">
                Create and manage workflow templates to standardize project processes across your organization.
              </p>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`btn btn-secondary${showFilters ? ' btn-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} />
            {showFilters ? 'Hide Filters' : 'Filters'}
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>
          {/* The noun here is "template" (matches the h1 and the editor's own save
              button) — the demo's "Create Workflow" label was cosmetics, not adopted. */}
          <button
            className="btn btn-primary"
            onClick={handleCreateTemplate}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Template
          </button>
        </div>
      </div>
      
      {/* Active Filter Pills */}
      {(filters.clientId || filters.ownerId || filters.search) && (
        <div className="active-filters">
          <span className="filter-label">Active filters:</span>
          {filters.clientId && (
            <span className="filter-pill active">
              Client: {getClientName(filters.clientId)}
              <button onClick={() => handleClientFilter('')}>×</button>
            </span>
          )}
          {filters.ownerId && (
            <span className="filter-pill active">
              Owner: {getOwnerName(filters.ownerId)}
              <button onClick={() => handleOwnerFilter('')}>×</button>
            </span>
          )}
          {filters.search && (
            <span className="filter-pill active">
              Search: {filters.search}
              <button onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '' })); }}>×</button>
            </span>
          )}
          <button className="btn-link" onClick={clearFilters}>Clear all</button>
        </div>
      )}
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-panel-header">
            <h3>Filter Templates</h3>
            {activeFilterCount > 0 && (
              <button className="btn-link" onClick={clearFilters}>Clear all</button>
            )}
          </div>
          <div className="filters-content">
            <div className="filter-group filter-group-search">
              <label>Search</label>
              <div className="search-input-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-input-icon" />
                <input
                  type="text"
                  placeholder="Search templates by name..."
                  className="search-input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    className="search-clear-button"
                    onClick={() => setSearchInput('')}
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            </div>
            <div className="filter-group">
              <label>Client</label>
              <select
                className="filter-select"
                value={filters.clientId}
                onChange={(e) => handleClientFilter(e.target.value)}
              >
                <option value="">All Clients</option>
                {clientOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Owner</label>
              <select
                className="filter-select"
                value={filters.ownerId}
                onChange={(e) => handleOwnerFilter(e.target.value)}
              >
                <option value="">All Owners</option>
                {employeeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Templates Table */}
      <div className="templates-table-container">
        {isLoading ? (
          <div className="loading-state">
            <span className="loading-spinner" aria-hidden="true" />
            <p>Loading templates...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="state-icon state-icon-danger">
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <p>Error loading templates: {error instanceof Error ? error.message : 'Unknown error'}</p>
            <button className="btn btn-primary" onClick={() => refetch()}>Retry</button>
          </div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <div className="state-icon">
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            <h3>No templates yet</h3>
            <p>Create your first workflow template to standardize project processes.</p>
            <button
              className="btn btn-primary"
              onClick={handleCreateTemplate}
            >
              <FontAwesomeIcon icon={faPlus} />
              Create Template
            </button>
          </div>
        ) : (
          <>
            <table className="templates-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Category</th>
                  <th>Owner</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr 
                    key={template.templateId}
                    onClick={() => handleRowClick(template.templateId)}
                    className="table-row-clickable"
                  >
                    <td>
                      <strong>{template.name}</strong>
                      {template.description && (
                        <div className="template-description">{template.description}</div>
                      )}
                    </td>
                    <td>{getClientName(template.client)}</td>
                    <td>
                      {template.category ? (
                        <span className="category-badge">{template.category}</span>
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </td>
                    <td>{getOwnerName(template.owner)}</td>
                    <td>{formatDateForDisplay(template.updatedAt || template.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="Edit Template"
                          aria-label="Edit template"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(template.templateId);
                          }}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          title="Delete Template"
                          aria-label="Delete template"
                          onClick={(e) => handleDeleteClick(template.templateId, template.name, e)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="delete-modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Template</h2>
              <button className="modal-close" onClick={cancelDelete}>×</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete <strong>{deleteTemplateName}</strong>? 
                This action cannot be undone and will remove the template and all its steps.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleteTemplateMutation.isPending}
              >
                {deleteTemplateMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

export default TemplatesPage;

